// One clickable unit per axis: the vertex pip + its axis label. Hovering
// or clicking ANY part of it activates the vertex.
//
// The pip and label are rendered as TWO independently positioned siblings,
// each with its own click + hover handlers, instead of a single wrapper
// with the label as a position:absolute child. The old structure put the
// label geometrically outside the wrapper's bounding box, so clicks on the
// label only reached the wrapper via DOM bubbling — fragile, and several
// CSS / browser-quirk paths could swallow the event. Rendering as siblings
// means each element's own listener fires the same handler directly, with
// no bubbling dependency.
//
// z-index lifts on hover so overlapping labels (common in the 20-axis
// Smell radar) never hide each other when the user is hunting.

type VertexLabelProps = {
  axis: string;
  /** Pip center in container coordinates (px). */
  vx: number;
  vy: number;
  fontSize: number;
  isHover: boolean;
  isSel: boolean;
  isActive: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick?: () => void;
};

const LABEL_OFFSET = 15; // px the label sits above the pip's center

export function VertexLabel({
  axis,
  vx,
  vy,
  fontSize,
  isHover,
  isSel,
  isActive,
  onHover,
  onLeave,
  onClick,
}: VertexLabelProps) {
  const accent = isSel || isActive;
  const pipSize = isHover || accent ? 11 : 9;
  const interactive = onClick != null;
  const zIndex = isHover ? 30 : accent ? 12 : 2;

  const handleClick = onClick
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
      }
    : undefined;

  const sharedHandlers = {
    onMouseEnter: onHover,
    onMouseLeave: onLeave,
    onClick: handleClick,
  };

  return (
    <>
      {/* Pip — centered exactly on (vx, vy). */}
      <div
        {...sharedHandlers}
        className="sr3-vertex"
        style={{
          position: "absolute",
          left: vx,
          top: vy,
          transform: "translate(-50%, -50%)",
          cursor: interactive ? "pointer" : "default",
          zIndex,
          padding: 4,
          userSelect: "none",
        }}
      >
        <div
          className="sr3-pip"
          style={{
            width: pipSize,
            height: pipSize,
            borderRadius: "50%",
            background: isSel
              ? "var(--accent)"
              : isActive
                ? "var(--accent-soft)"
                : "var(--panel)",
            border: `${isSel ? 2 : isActive ? 1.8 : 1}px ${
              isSel || isActive ? "solid" : isHover ? "solid" : "dashed"
            } ${accent || isHover ? "var(--accent-ink)" : "var(--line-strong)"}`,
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Label — positioned at (vx, vy - LABEL_OFFSET) as a separate
          sibling so it owns its own hit area and click handler. No more
          relying on bubbling through a parent whose bounding box doesn't
          contain it. */}
      <div
        {...sharedHandlers}
        className="sr3-vlabel sr3-vlabel-sibling"
        style={{
          position: "absolute",
          left: vx,
          top: vy - LABEL_OFFSET,
          transform: "translate(-50%, -50%)",
          cursor: interactive ? "pointer" : "default",
          zIndex,
          userSelect: "none",
          color: accent
            ? "var(--accent-ink)"
            : isHover
              ? "var(--ink)"
              : "var(--ink-3)",
          fontWeight: accent ? 700 : isHover ? 600 : 400,
          background: accent ? "var(--accent-soft)" : "var(--panel)",
          border: `1px solid ${
            accent ? "var(--accent-line)" : "var(--line)"
          }`,
          fontSize,
        }}
      >
        {axis}
      </div>
    </>
  );
}
